import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ChevronRight } from "lucide-react";
import Logo from "./Logo";
import PhoneMockup from "./PhoneMockup";

const EASE = [0.16, 1, 0.3, 1];

function Line({ children, active, fast, delay, z = "z-10", marginClass = "-mb-5" }) {
    return (
        <span
            className={`relative ${z} block overflow-hidden pb-6 ${marginClass}`}
        >
            <motion.span
                className="block will-change-transform"
                initial={fast ? false : { y: "160%" }}
                animate={active ? { y: "0%" } : { y: "160%" }}
                transition={{ duration: 1.1, ease: EASE, delay }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero({ active, fast, onNavigate }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const glowY = useTransform(scrollYProgress, [0, 1], [0, 60]);
    const [glowAccent, setGlowAccent] = useState(null);

    useEffect(() => {
        const onAccent = (e) => {
            const { accent, playing } = e.detail || {};
            setGlowAccent(playing ? accent : null);
        };
        window.addEventListener("rivvr:accent", onAccent);
        return () => window.removeEventListener("rivvr:accent", onAccent);
    }, []);

    const ctaButtons = (suffix = "", compact = false) => {
        const beta = (
            <motion.button
                data-testid={`hero-cta-btn${suffix}`}
                onClick={() => onNavigate("beta")}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.97 }}
                className={`group relative inline-flex shrink-0 items-center justify-center gap-2.5 overflow-hidden whitespace-nowrap rounded-[14px] border border-neon/50 bg-neon font-bold uppercase text-white shadow-[0_0_20px_rgba(3,135,226,0.4)] transition-colors duration-300 hover:border-white hover:bg-white hover:text-black hover:shadow-[0_0_35px_rgba(3,135,226,0.7)] ${compact ? "px-4 py-3 text-[10px] tracking-[0.14em]" : "px-7 py-4 text-xs tracking-[0.22em] sm:text-[13px]"}`}
            >
                <span className="relative">Get the Beta</span>
                <ChevronRight
                    className="relative h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                    strokeWidth={2.5}
                />
                <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full"
                />
            </motion.button>
        );
        const curation = (
            <motion.button
                data-testid={`hero-explore-btn${suffix}`}
                onClick={() => onNavigate("curation")}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.97 }}
                className={`group relative inline-flex shrink-0 items-center justify-center overflow-hidden whitespace-nowrap rounded-[14px] border border-white/15 bg-white/[0.04] font-semibold uppercase text-slate-200 backdrop-blur-md transition-colors duration-300 hover:border-neon/60 hover:bg-white/[0.08] hover:text-neon hover:shadow-[0_0_20px_rgba(3,135,226,0.25)] ${compact ? "gap-2 px-4 py-3 text-[10px] tracking-[0.14em]" : "gap-3 px-7 py-4 text-xs tracking-[0.22em] sm:text-[13px]"}`}
            >
                <span
                    aria-hidden
                    className="relative flex h-3 items-end gap-[3px]"
                >
                    {[0, 1, 2].map((b) => (
                        <span
                            key={b}
                            className="eq-bar eq-bar-paused"
                            style={{
                                animationDelay: `${b * 0.15}s`,
                            }}
                        />
                    ))}
                </span>
                <span className="relative">Hear the Curation</span>
            </motion.button>
        );
        return compact ? (
            <>
                {curation}
                {beta}
            </>
        ) : (
            <>
                {curation}
                {beta}
            </>
        );
    };

    return (
        <section
            ref={ref}
            id="experience"
            className="relative overflow-hidden px-4 pb-[26px] pt-24 sm:px-8 sm:pt-28 lg:px-12 lg:pb-[48px] lg:pt-32"
            data-testid="hero-section"
        >
            <motion.div
                style={{ y: glowY }}
                className="pointer-events-none absolute -top-40 left-1/2 h-[60vh] w-[90vw] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(3,135,226,0.10),transparent_60%)] blur-2xl"
            />
            <AnimatePresence>
                {glowAccent && (
                    <motion.div
                        key={glowAccent}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        style={{ y: glowY }}
                        className="pointer-events-none absolute -top-40 left-1/2 h-[60vh] w-[90vw] -translate-x-1/2 rounded-full blur-2xl"
                        aria-hidden="true"
                    >
                        <div
                            className="halo-breathe h-full w-full rounded-full"
                            style={{
                                background: `radial-gradient(ellipse at center, rgba(${glowAccent},0.14), transparent 60%)`,
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="mx-auto grid max-w-7xl items-center gap-[30px] md:grid-cols-12 md:gap-x-[7vw] md:gap-y-8 2xl:max-w-[88rem] 2xl:gap-14">
                <div className="relative z-10 md:col-span-7 md:pl-[7vw] 2xl:pl-28">
                    <motion.div
                        initial={fast ? false : { opacity: 0 }}
                        animate={active ? { opacity: 1 } : {}}
                        transition={{ delay: 0.2, duration: 0.8 }}
                        className="mb-[30px] sm:mb-8 flex items-center gap-3"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-neon opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-neon" />
                        </span>
                        <span className="whitespace-nowrap text-[clamp(0.55rem,2.3vw,0.75rem)] font-semibold uppercase tracking-[0.35em] text-dim">
                            Music + Film · One platform · Beta open
                        </span>
                    </motion.div>

                    <div className="relative">
                        <motion.div
                            initial={fast ? false : { opacity: 0, y: 16 }}
                            animate={active ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.3, duration: 0.9, ease: EASE }}
                            className="mb-4 sm:mb-[28px]"
                        >
                            <Logo heightClass="h-12 sm:h-16 2xl:h-20" />
                        </motion.div>

                        <div className="relative md:block">
                            <h1 className="min-w-0 flex-1 font-display font-black leading-[0.98] tracking-tight">
                                <Line active={active} fast={fast} delay={0.45} z="z-30" marginClass="-mb-[30px] sm:-mb-[22px]">
                                    <span className="text-[clamp(3rem,7vw,6.5rem)] text-ivory">
                                        WHERE
                                    </span>
                                </Line>
                                <Line active={active} fast={fast} delay={0.58} z="z-20" marginClass="mt-[6px] -mb-[18px] sm:-mb-[12px]">
                                    <span className="font-['Playfair_Display',serif] text-[clamp(3rem,7vw,6.5rem)] font-extrabold italic text-neon">
                                        Great Things
                                    </span>
                                </Line>
                                <Line active={active} fast={fast} delay={0.71}>
                                    <span className="text-[clamp(3rem,7vw,6.5rem)] text-ivory">
                                        STREAM<span className="text-neon">.</span>
                                    </span>
                                </Line>
                            </h1>
                            <img
                                src="/media/rivvr-app-icon.png"
                                alt="RIVVR app icon"
                                draggable={false}
                                data-testid="hero-app-icon"
                                className="absolute right-0 top-[6px] h-[123px] w-auto md:hidden"
                            />
                        </div>

                        <div
                            className="absolute bottom-[33px] right-0 top-0 flex w-[102px] flex-col items-center md:hidden"
                            data-testid="hero-app-aside"
                        >
                            <button
                                data-testid="hero-app-cta-btn"
                                onClick={() => onNavigate("beta")}
                                className="group relative -translate-x-[1px] mt-[14px] flex items-center gap-2 overflow-hidden whitespace-nowrap rounded-[10px] border border-white/20 bg-neon px-[7px] py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white shadow-[0_0_15px_rgba(3,135,226,0.35)] transition-all duration-300 hover:scale-105 hover:border-white/40 hover:shadow-[0_0_25px_rgba(3,135,226,0.6)]"
                            >
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-70" />
                                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                                </span>
                                <span className="relative">Get Beta</span>
                                <span
                                    aria-hidden
                                    className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full"
                                />
                            </button>
                        </div>
                    </div>

                    <motion.p
                        initial={fast ? false : { opacity: 0, y: 20 }}
                        animate={active ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.95, duration: 0.9, ease: EASE }}
                        className="mt-4 sm:mt-[30px] text-base leading-relaxed text-dim sm:text-lg"
                    >
                        The bar for great Music and Movies has been raised. Discover the best new sounds &amp; visions, that flow in a single current. Music curated by Professionals, not Algorithms. One Platform. One RIVVR...where great things stream.
                    </motion.p>

                    <motion.div
                        initial={fast ? false : { opacity: 0, y: 20 }}
                        animate={active ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
                        className="mt-8 hidden flex-wrap items-center gap-4 sm:mt-10 md:flex lg:flex-nowrap"
                    >
                        {ctaButtons()}
                    </motion.div>
                </div>

                <div className="flex justify-center isolate md:col-span-5 md:justify-end md:pr-[calc(7vw-32px)] lg:pr-[calc(7vw-48px)] min-[1376px]:pr-[7vw] 2xl:pr-28">
                    <PhoneMockup active={active} fast={fast} />
                </div>

                <motion.div
                    initial={fast ? false : { opacity: 0, y: 20 }}
                    animate={active ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
                    className="-mt-[6px] flex flex-wrap items-center justify-center gap-4 md:hidden"
                >
                    {ctaButtons("-mobile", true)}
                </motion.div>
            </div>
        </section>
    );
}
